import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import type { User } from 'firebase/auth';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  createProfileService,
  isValidEmail,
} from './profileService';
import type {
  ProfileData,
  ProfileDraft,
  ProfileValidationErrors,
} from './profileTypes';
import { profileQueryKey } from './profileTypes';
export { profileQueryKey } from './profileTypes';

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function createProfileDraft(profile: ProfileData): ProfileDraft {
  return {
    firstName: profile.firstName,
    instituteOfficialId: profile.instituteOfficialId,
    lastName: profile.lastName,
    preferredEmail: profile.preferredEmail,
  };
}

export function trimProfileDraft(draft: ProfileDraft): ProfileDraft {
  return {
    firstName: draft.firstName.trim(),
    instituteOfficialId: draft.instituteOfficialId.trim(),
    lastName: draft.lastName.trim(),
    preferredEmail: draft.preferredEmail.trim(),
  };
}

export function validateProfileDraft(
  draft: ProfileDraft,
): ProfileValidationErrors {
  const trimmedDraft = trimProfileDraft(draft);
  const errors: ProfileValidationErrors = {};

  if (!isValidEmail(trimmedDraft.preferredEmail)) {
    errors.preferredEmail = 'Enter a valid email address.';
  }

  if (!trimmedDraft.firstName) {
    errors.firstName = 'First name is required.';
  }

  if (!trimmedDraft.lastName) {
    errors.lastName = 'Last name is required.';
  }

  return errors;
}

function hasValidationErrors(errors: ProfileValidationErrors) {
  return Object.keys(errors).length > 0;
}

function buildOptimisticProfile(
  profile: ProfileData,
  draft: ProfileDraft,
): ProfileData {
  return {
    ...profile,
    firstName: draft.firstName,
    instituteOfficialId: draft.instituteOfficialId,
    lastName: draft.lastName,
    preferredEmail: draft.preferredEmail,
    rawProfile: {
      ...(profile.rawProfile ?? {}),
      first_name: draft.firstName,
      institute_official_id: draft.instituteOfficialId,
      is_instructor: profile.isInstructor,
      last_name: draft.lastName,
      preferred_email: draft.preferredEmail,
    },
  };
}

function getReturnTo(location: ReturnType<typeof useLocation>) {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function useProfile() {
  const { getIdToken, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<ProfileDraft>({
    firstName: '',
    instituteOfficialId: '',
    lastName: '',
    preferredEmail: '',
  });

  const createClient = () => {
    if (!user) {
      throw createDataError('You must be signed in to view your profile.');
    }

    const returnTo = getReturnTo(location);

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });
  };

  const profileQuery = useQuery<ProfileData, ApiError>({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!kuduUserId) {
        throw createDataError('You must be signed in to view your profile.');
      }

      return createProfileService(createClient()).getProfile({
        userId: kuduUserId,
      });
    },
    queryKey: profileQueryKey(kuduUserId),
    retry: false,
  });

  const profile = profileQuery.data ?? null;

  useEffect(() => {
    if (profile && !isEditing) {
      setDraft(createProfileDraft(profile));
    }
  }, [isEditing, profile]);

  const validationErrors = useMemo(
    () => validateProfileDraft(draft),
    [draft],
  );

  const saveMutation = useMutation<ProfileData | null, ApiError, ProfileDraft>({
    mutationFn: async (nextDraft) => {
      if (!profile || !kuduUserId) {
        throw createDataError('Profile data is not ready to save.');
      }

      return createProfileService(createClient()).updateProfile({
        profile: {
          firstName: nextDraft.firstName,
          instituteOfficialId: nextDraft.instituteOfficialId,
          isInstructor: profile.isInstructor,
          lastName: nextDraft.lastName,
          preferredEmail: nextDraft.preferredEmail,
        },
        userId: kuduUserId,
      });
    },
    onSuccess: (updatedProfile, submittedDraft) => {
      if (!profile || !kuduUserId) {
        return;
      }

      const nextProfile =
        updatedProfile ?? buildOptimisticProfile(profile, submittedDraft);

      queryClient.setQueryData(profileQueryKey(kuduUserId), nextProfile);
      setDraft(createProfileDraft(nextProfile));
      setIsEditing(false);
    },
  });

  function startEditing() {
    if (profile) {
      setDraft(createProfileDraft(profile));
    }

    setIsEditing(true);
    saveMutation.reset();
  }

  function cancelEditing() {
    if (profile) {
      setDraft(createProfileDraft(profile));
    }

    setIsEditing(false);
    saveMutation.reset();
  }

  function updateField(field: keyof ProfileDraft, value: string) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  }

  async function saveProfile() {
    const trimmedDraft = trimProfileDraft(draft);
    const errors = validateProfileDraft(trimmedDraft);

    if (hasValidationErrors(errors)) {
      return false;
    }

    try {
      await saveMutation.mutateAsync(trimmedDraft);
      return true;
    } catch {
      return false;
    }
  }

  const authError =
    !loading && !user
      ? createDataError('You must be signed in to view your profile.')
      : null;
  const error = profileQuery.error ?? authError;
  const formStatusMessage = saveMutation.error
    ? saveMutation.error.message || 'Unable to save your profile.'
    : hasValidationErrors(validationErrors)
      ? 'Please fix the highlighted profile fields before saving.'
      : saveMutation.isSuccess
        ? 'Profile saved.'
        : '';

  return {
    canSave:
      isEditing &&
      !saveMutation.isPending &&
      !hasValidationErrors(validationErrors),
    cancelEditing,
    draft,
    error,
    formStatusMessage,
    isEditing,
    isLoading: loading || (Boolean(kuduUserId) && profileQuery.isPending),
    isSaving: saveMutation.isPending,
    profile,
    refetch: profileQuery.refetch,
    saveError: saveMutation.error,
    saveProfile,
    startEditing,
    updateField,
    user: user as User | null,
    validationErrors,
  };
}
